import React, { createContext, useCallback, useContext, useState } from 'react';
import * as authApi from '../api/authApi';
import { TOKEN_KEY } from '../api/client';
import { decodeJwt, getEmail, getRole, isExpired } from '../utils/jwt';

interface AdminUser {
  email: string;
  role: string;
}

interface AuthContextValue {
  token: string | null;
  user: AdminUser | null;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  token: null,
  user: null,
  isAdmin: false,
  login: async () => {},
  logout: () => {},
});

function readStoredAuth(): { token: string | null; user: AdminUser | null } {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return { token: null, user: null };
  const payload = decodeJwt(token);
  const role = getRole(payload);
  if (!payload || isExpired(payload) || role !== 'admin') {
    localStorage.removeItem(TOKEN_KEY);
    return { token: null, user: null };
  }
  return { token, user: { email: getEmail(payload) ?? '', role } };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [auth, setAuth] = useState(readStoredAuth);

  const login = useCallback(async (email: string, password: string) => {
    const data = await authApi.login(email, password);
    const token: string | undefined =
      data?.token ?? data?.accessToken ?? data?.access_token ?? data?.data?.token;
    if (!token) throw new Error(data?.message || 'Login failed — no token returned');

    const payload = decodeJwt(token);
    const role = getRole(payload);
    if (role !== 'admin') throw new Error('Not authorized');

    localStorage.setItem(TOKEN_KEY, token);
    setAuth({ token, user: { email: getEmail(payload) ?? email, role } });
  }, []);

  const logout = useCallback(() => {
    authApi.logout();
    setAuth({ token: null, user: null });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token: auth.token,
        user: auth.user,
        isAdmin: auth.user?.role === 'admin',
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
